import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom'; // ✅ Import Portal

// Helper parse JWT
const parseJwt = (token) => {
    try {
        if (!token) return null;
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) { return null; }
};

export default function NotificationPage() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); 
    const [userRole, setUserRole] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, notiId: null });

    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    useEffect(() => {
        const token = localStorage.getItem('skillmatch_token');
        if (token) {
            const decoded = parseJwt(token);
            setUserRole(decoded?.user?.role || 'candidate');
        }

        const fetchNotifications = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/notifications`, {
                    headers: { 'x-auth-token': token }
                });
                const data = await res.json();
                if (data.notifications) {
                    setNotifications(data.notifications);
                }
            } catch (error) {
                console.error("Lỗi tải thông báo:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchNotifications();
    }, [API_URL]);

    const handleItemClick = async (noti) => {
        if (noti.link) navigate(noti.link);
        if (!noti.isRead) {
            setNotifications(prev => prev.map(n => n._id === noti._id ? { ...n, isRead: true } : n));
            try {
                const token = localStorage.getItem('skillmatch_token');
                await fetch(`${API_URL}/api/notifications/${noti._id}/read`, {
                    method: 'PUT',
                    headers: { 'x-auth-token': token }
                });
            } catch (e) { console.error(e); }
        }
    };

    const markAllRead = async () => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        try {
            const token = localStorage.getItem('skillmatch_token');
            await fetch(`${API_URL}/api/notifications/read-all`, {
                method: 'PUT',
                headers: { 'x-auth-token': token }
            });
        } catch (e) { console.error(e); }
    };

    const openDeleteModal = (e, id) => {
        e.stopPropagation();
        setDeleteModal({ isOpen: true, notiId: id });
    };

    const confirmDelete = async () => {
        const id = deleteModal.notiId;
        if (!id) return;
        setDeleteModal({ isOpen: false, notiId: null });
        setNotifications(prev => prev.filter(n => n._id !== id));
        try {
            const token = localStorage.getItem('skillmatch_token');
            await fetch(`${API_URL}/api/notifications/${id}`, {
                method: 'DELETE',
                headers: { 'x-auth-token': token }
            });
        } catch (e) { console.error(e); }
    };

    const filteredList = filter === 'all' ? notifications : notifications.filter(n => !n.isRead);

    const pageTitle = "Thông báo";
    const pageSubtitle = userRole === 'business' 
        ? "Cập nhật mới nhất về ứng viên và hoạt động tuyển dụng." 
        : "Cập nhật tin tức mới nhất về công việc và hồ sơ của bạn.";

    return (
        <div className="min-h-screen bg-[#F3F4F6] font-sans text-[#111827] pb-10">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                
                {/* HEADER */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
                        <p className="text-gray-500 text-sm mt-1">{pageSubtitle}</p>
                    </div>
                    {notifications.length > 0 && (
                        <button 
                            onClick={markAllRead}
                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition shadow-sm flex items-center gap-2"
                        >
                            <span className="material-symbols-outlined text-[18px]">done_all</span>
                            Đánh dấu tất cả đã đọc
                        </button>
                    )}
                </div>

                {/* TABS */}
                <div className="flex gap-6 border-b border-gray-200 mb-6">
                    <button onClick={() => setFilter('all')} className={`pb-3 text-sm font-medium transition-colors relative ${filter === 'all' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                        Tất cả
                        {filter === 'all' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>}
                    </button>
                    <button onClick={() => setFilter('unread')} className={`pb-3 text-sm font-medium transition-colors relative ${filter === 'unread' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                        Chưa đọc
                        {filter === 'unread' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>}
                    </button>
                </div>

                {/* LIST */}
                {loading ? (
                    <div className="text-center py-12 text-gray-500">Đang tải thông báo...</div>
                ) : filteredList.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-3xl text-gray-400">notifications_off</span>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">Không có thông báo nào</h3>
                        <p className="text-gray-500 text-sm mt-1">Bạn đã xem hết tất cả thông báo hoặc chưa có tin mới.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredList.map((noti) => (
                            <div key={noti._id} onClick={() => handleItemClick(noti)} className={`group relative p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md flex gap-4 ${noti.isRead ? 'bg-white border-gray-200 hover:border-blue-300' : 'bg-white border-blue-200 shadow-sm ring-1 ring-blue-50'}`}>
                                <div className={`mt-1 w-3 h-3 rounded-full flex-shrink-0 ${!noti.isRead ? 'bg-blue-600 shadow-sm' : 'bg-gray-300'}`}></div>
                                <div className="flex-1 pr-8">
                                    <div className="flex justify-between items-start">
                                        <h4 className={`text-sm mb-1 ${!noti.isRead ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{noti.title}</h4>
                                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{new Date(noti.createdAt).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                                    </div>
                                    <p className={`text-sm leading-relaxed ${!noti.isRead ? 'text-gray-800' : 'text-gray-500'}`}>{noti.message}</p>
                                </div>
                                <button onClick={(e) => openDeleteModal(e, noti._id)} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all" title="Xóa thông báo"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ✅ MODAL XÓA (Dùng Portal để che cả Navbar) */}
            {deleteModal.isOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                     {/* Nền đen mờ nhẹ, KHÔNG BLUR */}
                     <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteModal({isOpen: false, notiId: null})}></div>
                     
                     <div className="bg-white rounded-xl shadow-xl w-full max-w-sm relative z-10 overflow-hidden animate-fadeIn">
                        <div className="p-6 text-center">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
                                <span className="material-symbols-outlined text-2xl">delete</span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Xóa thông báo?</h3>
                            <p className="text-gray-500 text-sm">Bạn có chắc chắn muốn xóa thông báo này không? Hành động này không thể hoàn tác.</p>
                        </div>
                        <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-center border-t border-gray-100">
                             <button 
                                onClick={() => setDeleteModal({isOpen: false, notiId: null})}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                             >
                                Hủy
                             </button>
                             <button 
                                onClick={confirmDelete}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
                             >
                                Xóa
                             </button>
                        </div>
                     </div>
                </div>,
                document.body
            )}
            
            <style>{`
                @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
                .animate-fadeIn { animation: fadeIn 0.15s ease-out forwards; }
            `}</style>
        </div>
    );
}